import { useCallback, useEffect, useRef, useState } from 'react';

export const useClientRect = (): [
    DOMRect | null,
    (node: HTMLElement | null) => void,
    HTMLElement | null,
] => {
    const element = useRef<HTMLElement | null>(null);
    const [rect, setRect] = useState<DOMRect | null>(null);

    const handleResize = useCallback(() => {
        if (element.current) {
            setRect(element.current.getBoundingClientRect());
        }
    }, []);

    useEffect(() => {
        if (!rect) {
            return;
        }

        /*
         * we have to wait here because element size could be changed after render
         * and there is no other way to handle this situation
         */
        const to = setTimeout(() => {
            const newRect = element.current?.getBoundingClientRect();

            if (
                newRect &&
                rect &&
                (newRect.width !== rect.width ||
                    newRect.height !== rect.height ||
                    newRect.x !== rect.x ||
                    newRect.y !== rect.y)
            ) {
                setRect(newRect);
            }
        }, 0);

        return () => clearTimeout(to);
    });

    const ref = useCallback(
        (node: HTMLElement | null) => {
            if (node !== null) {
                window.addEventListener('resize', handleResize);
                setRect(node.getBoundingClientRect());
            } else {
                window.removeEventListener('resize', handleResize);
                setRect(null);
            }
            element.current = node;
        },
        [handleResize]
    );

    return [rect, ref, element.current];
};
